import { useEffect, useState } from 'react'

export interface CountdownToMidnight {
  hours: number
  minutes: number
  totalMs: number
}

function compute(): CountdownToMidnight {
  const now = new Date()
  // Backend resets the AI rate-limit counter at 00:00 UTC (utils/rate_limit.py).
  // Counting against UTC midnight keeps the displayed timer in sync with that
  // reset regardless of the user's local timezone.
  const midnightUtc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0, 0, 0, 0,
  )
  const totalMs = Math.max(0, midnightUtc - now.getTime())
  const totalMinutes = Math.floor(totalMs / 60_000)
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    totalMs,
  }
}

/**
 * Live countdown to the next UTC midnight. Ticks at 1Hz but only triggers a
 * re-render when the displayed (hours, minutes) actually changes — so a long
 * countdown costs at most one render per minute. Pass `active=false` to halt
 * the interval entirely when the consumer is not visible.
 */
export function useCountdownToMidnightUtc(active: boolean): CountdownToMidnight {
  const [value, setValue] = useState<CountdownToMidnight>(compute)

  useEffect(() => {
    if (!active) return
    setValue(compute())
    const id = setInterval(() => {
      setValue((prev) => {
        const next = compute()
        if (next.hours === prev.hours && next.minutes === prev.minutes) {
          // Returning the same reference is a setState bail-out — React skips
          // the render. Keeps the timer at minute precision without
          // re-rendering every second.
          return prev
        }
        return next
      })
    }, 1000)
    return () => clearInterval(id)
  }, [active])

  return value
}
